/**
 * Input sanitization and XSS protection utilities
 */

import { filterAvailableRides, filterAvailableShows } from "./availabilityRules";

// HTML entities to escape
const htmlEntities = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;',
  '`': '&#x60;',
  '=': '&#x3D;'
};

/**
 * Escape HTML entities to prevent XSS
 * @param {string} str - String to escape
 * @returns {string} - Escaped string
 */
export const escapeHtml = (str) => {
  if (typeof str !== 'string') return str;
  return str.replace(/[&<>"'`=\/]/g, (char) => htmlEntities[char]);
};

/**
 * Sanitize text input (removes script tags and dangerous content)
 * @param {string} input - Raw input string
 * @returns {string} - Sanitized string
 */
export const sanitizeText = (input) => {
  if (typeof input !== 'string') return input;
  
  // Remove script tags and dangerous content
  let sanitized = input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .replace(/<iframe/gi, '')
    .replace(/<object/gi, '')
    .replace(/<embed/gi, '')
    .replace(/<form/gi, '')
    .replace(/<input/gi, '')
    .replace(/<textarea/gi, '')
    .replace(/<select/gi, '')
    .replace(/<button/gi, '');
  
  // Escape HTML entities
  return escapeHtml(sanitized);
};

/**
 * Sanitize email input
 * @param {string} email - Email string
 * @returns {string} - Sanitized email (allows partial input during typing)
 */
export const sanitizeEmail = (email) => {
  if (typeof email !== 'string') return '';
  
  // Basic sanitization for email input - allow partial emails during typing
  const sanitized = sanitizeText(email.trim());
  
  // Only validate complete email format if it contains @ and .
  if (sanitized.includes('@') && sanitized.includes('.')) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(sanitized) ? sanitized : sanitized;
  }
  
  // Allow partial input during typing
  return sanitized;
};

/**
 * Sanitize phone number input
 * @param {string} phone - Phone string
 * @returns {string} - Sanitized phone number
 */
export const sanitizePhone = (phone) => {
  if (typeof phone !== 'string') return '';
  
  // Remove all non-digit characters except +, -, (, ), and space
  const sanitized = phone.replace(/[^\d\s\+\-\(\)]/g, '');
  
  // Limit length to prevent abuse
  return sanitized.substring(0, 20);
};

/**
 * Sanitize date input
 * @param {string} date - Date string
 * @returns {string} - Sanitized date or empty string if invalid
 */
export const sanitizeDate = (date) => {
  if (typeof date !== 'string') return '';
  
  // Allow MM/DD/YYYY or MM/dd/yyyy format
  const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
  
  // For dates, we only need basic sanitization, not HTML escaping
  const sanitized = date.trim().replace(/[<>]/g, ''); // Remove < and > but keep /
  
  return dateRegex.test(sanitized) ? sanitized : '';
};

/**
 * Sanitize number input
 * @param {string|number} input - Number input
 * @returns {number|null} - Sanitized number or null if invalid
 */
export const sanitizeNumber = (input) => {
  if (typeof input === 'number') {
    return isFinite(input) ? input : null;
  }
  
  if (typeof input !== 'string') return null;
  
  const sanitized = sanitizeText(input.trim());
  const num = parseInt(sanitized, 10);
  
  return isFinite(num) ? num : null;
};

/**
 * Sanitize array of strings
 * @param {Array} arr - Array of strings
 * @returns {Array} - Array of sanitized strings
 */
export const sanitizeArray = (arr) => {
  if (!Array.isArray(arr)) return [];
  
  return arr
    .filter(item => typeof item === 'string')
    .map(item => sanitizeText(item))
    .filter(item => item.length > 0);
};

/**
 * Sanitize form data object
 * @param {Object} formData - Raw form data
 * @returns {Object} - Sanitized form data
 */
export const sanitizeFormData = (formData) => {
  if (!formData || typeof formData !== 'object') return {};
  
  const sanitized = {};
  
  for (const [key, value] of Object.entries(formData)) {
    if (value === null || value === undefined) continue;
    
    switch (key) {
      case 'email':
        sanitized[key] = sanitizeEmail(value);
        break;
      case 'phoneNumber':
      case 'phone':
        sanitized[key] = sanitizePhone(value);
        break;
      case 'startDate':
      case 'endDate':
        sanitized[key] = sanitizeDate(value);
        break;
      case 'celebrationDate':
        // customDate stores yyyy-MM-dd; also accept Date objects
        if (typeof value === 'string') {
          const isoMatch = value.trim().match(/^\d{4}-\d{2}-\d{2}$/);
          sanitized[key] = isoMatch ? value.trim() : sanitizeDate(value);
        } else if (value && typeof value.toISOString === 'function') {
          sanitized[key] = value.toISOString().slice(0, 10);
        } else {
          sanitized[key] = '';
        }
        break;
      case 'adults':
      case 'kids':
      case 'numBoys':
      case 'numGirls':
      case 'numAdultMales':
      case 'numAdultFemales':
      case 'mkDays':
      case 'hsDays':
      case 'epcotDays':
      case 'akDays':
      case 'tlDays':
      case 'bbDays':
        sanitized[key] = sanitizeNumber(value);
        break;
      case 'allergies':
      case 'parks':
      case 'mustRides':
      case 'mustSeeShows':
      case 'mustTryRestaurants':
      case 'rideStrategy':
      case 'showStrategy':
      case 'restaurantStrategy':
      case 'diningTime':
      case 'parkStartTime':
      case 'breakTime':
      case 'enchantedExperiences':
      case 'mustExperience':
      case 'celebrationDetails':
      case 'disabilities':
      case 'additionalInfoDetails':
        sanitized[key] = Array.isArray(value) ? sanitizeArray(value) : sanitizeText(value);
        break;
      case 'agesOfBoys':
      case 'agesOfGirls':
        sanitized[key] = Array.isArray(value) ? value.map(age => sanitizeNumber(age)).filter(age => age !== null) : [];
        break;
      default:
        sanitized[key] = sanitizeText(value);
    }
  }
  
  return sanitized;
};

/**
 * Validate and sanitize questionnaire responses before submission
 * @param {Object} responses - Raw questionnaire responses
 * @returns {Object} - Validated and sanitized responses
 */
export const validateAndSanitizeQuestionnaire = (responses) => {
  if (!responses || typeof responses !== 'object') {
    throw new Error('Invalid questionnaire responses');
  }
  
  const sanitized = sanitizeFormData(responses);
  
  // Helper function to get value from organized data structure
  const getValueFromOrganizedData = (data, fieldName) => {
    // Check if data is organized (has nested structure)
    if (data.basicInformation || data.parkVisitPlans || data.attractionRideStrategy) {
      // Map field names to their organized locations
      const fieldMapping = {
        fullName: data.basicInformation?.fullName,
        email: data.basicInformation?.email,
        phoneNumber: data.basicInformation?.phoneNumber,
        startDate: data.basicInformation?.startDate,
        endDate: data.basicInformation?.endDate,
        adults: data.groupInfo?.adults,
        numAdultMales: data.groupInfo?.numAdultMales,
        numAdultFemales: data.groupInfo?.numAdultFemales,
        kids: data.groupInfo?.kids,
        numBoys: data.groupInfo?.numBoys,
        numGirls: data.groupInfo?.numGirls,
        agesOfBoys: data.groupInfo?.agesOfBoys,
        agesOfGirls: data.groupInfo?.agesOfGirls,
        ticketType: data.parkTicketAccess?.ticketType,
        hasParkHopper: data.parkTicketAccess?.hasParkHopper,
        parks: data.parkVisitPlans?.parks,
        mkDays: data.parkVisitPlans?.mkDays,
        hsDays: data.parkVisitPlans?.hsDays,
        epcotDays: data.parkVisitPlans?.epcotDays,
        akDays: data.parkVisitPlans?.akDays,
        tlDays: data.parkVisitPlans?.tlDays,
        bbDays: data.parkVisitPlans?.bbDays,
        transportation: data.accommodation?.transportation,
        stayingOnProperty: data.accommodation?.stayingOnProperty,
        resort: data.accommodation?.resort,
        otherHotel: data.accommodation?.otherHotel,
        rideStrategy: data.attractionRideStrategy?.rideStrategy,
        showStrategy: data.showFireworkStrategy?.showStrategy,
        mustRides: data.attractionRideStrategy?.mustRides,
        lightningLane: data.attractionRideStrategy?.lightningLane,
        mustSeeShows: data.showFireworkStrategy?.mustSeeShows,
        restaurantStrategy: data.diningPreferences?.restaurantStrategy,
        mustTryRestaurants: data.diningPreferences?.mustTryRestaurants,
        diningPlanInterest: data.diningPreferences?.diningPlanInterest,
        diningTime: data.diningPreferences?.diningTime,
        allergies: data.diningPreferences?.allergies,
        parkStartTime: data.timeEnergy?.parkStartTime,
        breakTime: data.timeEnergy?.breakTime,
        enchantedExperiences: data.specialtyExperiences?.enchantedExperiences,
        mustExperience: data.specialtyExperiences?.mustExperience,
        celebration: data.specialConsiderations?.celebration,
        celebrationDetails: data.specialConsiderations?.celebrationDetails,
        celebrationDate: data.specialConsiderations?.celebrationDate,
        hasDisability: data.specialConsiderations?.hasDisability,
        disabilities: data.specialConsiderations?.disabilities,
        additionalInfo: data.specialConsiderations?.additionalInfo,
        additionalInfoDetails: data.specialConsiderations?.additionalInfoDetails
      };
      
      return fieldMapping[fieldName] !== undefined ? fieldMapping[fieldName] : data[fieldName];
    }
    
    // Return direct access for flat data
    return data[fieldName];
  };
  
  // Required field validation
  const requiredFields = ['fullName', 'email', 'startDate', 'endDate', 'adults'];
  const missingFields = requiredFields.filter(field => !getValueFromOrganizedData(sanitized, field));
  
  if (missingFields.length > 0) {
    throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
  }
  
  // Email validation
  const email = getValueFromOrganizedData(sanitized, 'email');
  if (!email) {
    throw new Error('Invalid email address');
  }
  
  // Date validation
  const startDate = getValueFromOrganizedData(sanitized, 'startDate');
  const endDate = getValueFromOrganizedData(sanitized, 'endDate');
  if (!startDate || !endDate) {
    throw new Error('Invalid date format');
  }
  
  // Number validation
  const adults = getValueFromOrganizedData(sanitized, 'adults');
  if (!adults || adults < 1) {
    throw new Error('At least one adult is required');
  }

  // Strip disabled rides/shows from payload (closure/opening rules)
  const rideList = getValueFromOrganizedData(sanitized, 'mustRides');
  if (Array.isArray(rideList) && rideList.length > 0) {
    const { filtered } = filterAvailableRides(rideList, sanitized);
    if (sanitized.attractionRideStrategy) {
      sanitized.attractionRideStrategy = { ...sanitized.attractionRideStrategy, mustRides: filtered };
    }
  }
  const showList = getValueFromOrganizedData(sanitized, 'mustSeeShows');
  if (Array.isArray(showList) && showList.length > 0) {
    const { filtered } = filterAvailableShows(showList, sanitized);
    if (sanitized.showFireworkStrategy) {
      sanitized.showFireworkStrategy = { ...sanitized.showFireworkStrategy, mustSeeShows: filtered };
    }
  }

  return sanitized;
}; 